<template>
  <component
    :is="elementType"
    class="inline-block rounded-md text-center uppercase tracking-wider no-underline cursor-pointer hover:no-underline"
    :class="[
      outline ? outlineClasses[variant] : filledClasses[variant],
      outline
        ? 'font-semibold transition-colors'
        : 'font-bold transition-opacity',
      size === 'sm' ? 'px-3 py-1' : size === 'lg' ? 'px-6 py-3' : '',
      block ? 'w-full' : '',
      disabled ? 'pointer-events-none cursor-not-allowed opacity-50' : '',
    ]"
    v-bind="bindings"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';

const NuxtLink = resolveComponent('NuxtLink');

type Variant = 'primary' | 'secondary' | 'danger';
type Size = 'sm' | 'lg' | 'custom';

const props = withDefaults(
  defineProps<{
    variant?: Variant;
    outline?: boolean;
    size?: Size;
    block?: boolean;
    to?: RouteLocationRaw;
    href?: string;
    type?: 'button' | 'submit' | 'reset';
    disabled?: boolean;
  }>(),
  {
    variant: 'primary',
    outline: false,
    size: 'lg',
    block: false,
    to: undefined,
    href: undefined,
    type: 'button',
    disabled: false,
  },
);

const elementType = computed(() => {
  if (props.to != null) return NuxtLink;
  if (props.href != null) return 'a';
  return 'button';
});

const filledClasses: Record<Variant, string> = {
  primary: 'bg-brand text-onyx hover:opacity-90',
  secondary: 'bg-depth-2 text-mouse hover:bg-depth-3',
  danger: 'bg-danger text-mouse hover:opacity-90',
};

const outlineClasses: Record<Variant, string> = {
  primary: 'border border-brand/40 text-brand hover:bg-brand/5',
  secondary: 'border border-mouse bg-depth-0 text-mouse hover:bg-depth-2',
  danger: 'border border-danger text-danger-fg hover:bg-danger/5',
};

const bindings = computed(() => {
  const out: Record<string, unknown> = {};
  if (props.to != null) out.to = props.to;
  if (props.href != null) out.href = props.href;
  if (elementType.value === 'button') {
    out.type = props.type;
    if (props.disabled) out.disabled = true;
  }
  return out;
});
</script>
